import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { mockPartners } from '@/mocks/partners';
import { mockOffers } from '@/mocks/offers';
import { mockLeads } from '@/mocks/leads';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import BaseSelect from '@/components/base/BaseSelect';
import { SkeletonList } from '@/components/Skeleton';
import {
  ArrowLeft, ArrowUpRight, Mail, Phone, Globe, Users, FileText, Activity,
  Wallet, Edit2, X, Send, Save, Loader2, CheckCircle, AlertTriangle, Trash2
} from 'lucide-react';

interface PartnerProfile {
  id: string;
  full_name: string | null;
  email: string;
  role: string;
  created_at: string;
}

interface DbOffer {
  id: string;
  partner_id: string;
  vertical_id: string;
  geo_id: string;
  payout_model: string;
  cpl_value: number | null;
  cpa_value: number | null;
  revshare_percent: number | null;
  flat_subscription_amount: number | null;
  min_daily_capacity: number | null;
  max_daily_capacity: number | null;
  priority_score: number;
  status: string;
  active: boolean;
  start_date: string;
  end_date: string | null;
  created_at: string;
  updated_at: string;
}

interface DbLead {
  id: string;
  assigned_partner_id: string | null;
  status: string;
  routing_status: string;
  created_at: string;
  raw_payload: Record<string, unknown> | null;
}

interface DbPayout {
  id: string;
  partner_id: string;
  payout_model: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
}

function PartnerInviteModal({ partnerId, onInvited }: { partnerId: string; onInvited: () => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('partner_staff');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleInvite = async () => {
    setError('');
    setSending(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('invite_partner_user', {
        body: {
          email,
          full_name: fullName,
          partner_id: partnerId,
          role,
          password: password || undefined,
        },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setSending(false);
      setSent(true);
      onInvited();
      setTimeout(() => { setOpen(false); setSent(false); setEmail(''); setFullName(''); }, 2000);
    } catch (err: any) {
      setSending(false);
      setError(err?.message || 'Failed to send invite');
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-all"
        style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
      >
        <Users size={12} />
        Invite User
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--overlay-backdrop)', backdropFilter: 'blur(8px)' }}
    >
      <div className="rounded-xl border w-full max-w-md p-6 space-y-4 animate-scale-in"
        style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)' }}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Invite Partner User</h3>
          <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-tertiary)' }}>
            <X size={14} />
          </button>
        </div>
        {sent && (
          <div className="flex items-center gap-2 p-3 rounded-lg border text-sm"
            style={{ borderColor: 'var(--success-border)', background: 'var(--success-bg)', color: 'var(--success-text)' }}
          >
            <CheckCircle size={16} />
            <span>Invite sent! They will receive a magic link to sign in.</span>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg border text-sm"
            style={{ borderColor: 'var(--danger-border)', background: 'var(--danger-bg)', color: 'var(--danger-text)' }}
          >
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}
        {!sent && (
          <>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="e.g. John Smith"
                className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="john@company.com"
                className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Role</label>
              <BaseSelect
                value={role}
                onChange={val => setRole(val)}
                options={[
                  { value: 'partner_principal', label: 'Partner Principal (owner)' },
                  { value: 'partner_staff', label: 'Partner Staff' },
                ]}
              />
            </div>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Password (optional)</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Leave empty to send magic link"
                className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
              />
            </div>
          </>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs rounded-lg transition-colors" style={{ color: 'var(--text-tertiary)' }}>
            Cancel
          </button>
          {!sent && (
            <button
              onClick={handleInvite}
              disabled={sending || !email || !fullName}
              className="flex items-center gap-1.5 px-4 py-2 text-xs rounded-lg transition-all disabled:opacity-40"
              style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {sending ? 'Creating...' : password ? 'Create Account' : 'Send Invite'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const tabs = [
  { id: 'info', label: 'Info', icon: <Globe size={14} /> },
  { id: 'offers', label: 'Offers', icon: <FileText size={14} /> },
  { id: 'users', label: 'Users', icon: <Users size={14} /> },
  { id: 'activity', label: 'Activity', icon: <Activity size={14} /> },
  { id: 'payouts', label: 'Payouts', icon: <Wallet size={14} /> },
];

export default function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [editing, setEditing] = useState(false);
  const [partnerUsers, setPartnerUsers] = useState<PartnerProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Real data states
  const [partner, setPartner] = useState(mockPartners.find(p => p.id === id) || null);
  const [offers, setOffers] = useState<DbOffer[]>([]);
  const [leads, setLeads] = useState<DbLead[]>([]);
  const [payouts, setPayouts] = useState<DbPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [editData, setEditData] = useState(() => ({
    display_name: partner?.display_name || '',
    legal_name: partner?.legal_name || '',
    country: partner?.country || '',
    vat_id: partner?.vat_id || '',
    billing_currency: partner?.billing_currency || 'EUR',
    primary_contact_name: partner?.primary_contact_name || '',
    primary_email: partner?.primary_email || '',
    primary_phone: partner?.primary_phone || '',
    internal_notes: partner?.internal_notes || '',
  }));

  // Fetch partner from Supabase
  const fetchPartner = useCallback(async () => {
    if (!id) return;
    try {
      const { data, error: supaErr } = await supabase.from('partners').select('*').eq('id', id).maybeSingle();
      if (supaErr) throw supaErr;
      if (data) {
        setPartner(data);
        setEditData({
          display_name: data.display_name || '',
          legal_name: data.legal_name || '',
          country: data.country || '',
          vat_id: data.vat_id || '',
          billing_currency: data.billing_currency || 'EUR',
          primary_contact_name: data.primary_contact_name || '',
          primary_email: data.primary_email || '',
          primary_phone: data.primary_phone || '',
          internal_notes: data.internal_notes || '',
        });
      }
    } catch {
      const mock = mockPartners.find(p => p.id === id);
      if (mock) setPartner(mock);
    }
  }, [id]);

  const fetchTabData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [offersRes, leadsRes, payoutsRes] = await Promise.all([
        supabase.from('offers').select('*').eq('partner_id', id),
        supabase.from('leads').select('id, assigned_partner_id, status, routing_status, created_at, raw_payload').eq('assigned_partner_id', id).order('created_at', { ascending: false }),
        supabase.from('payouts').select('*').eq('partner_id', id).order('created_at', { ascending: false }),
      ]);
      setOffers(offersRes.data || []);
      setLeads(leadsRes.data || []);
      setPayouts(payoutsRes.data || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  const handleSave = async () => {
    if (!id || !partner) return;
    try {
      const { error: supaErr } = await supabase.from('partners').update({
        display_name: editData.display_name,
        legal_name: editData.legal_name,
        country: editData.country,
        vat_id: editData.vat_id || null,
        billing_currency: editData.billing_currency,
        primary_contact_name: editData.primary_contact_name || null,
        primary_email: editData.primary_email || null,
        primary_phone: editData.primary_phone || null,
        internal_notes: editData.internal_notes || null,
      }).eq('id', id);
      if (supaErr) throw supaErr;
      await fetchPartner();
      setEditing(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const { error: delErr } = await supabase.from('partners').delete().eq('id', id);
      if (delErr) throw delErr;
      navigate('/partners');
    } catch (err: any) {
      setError(err?.message || 'Failed to delete partner');
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const fetchPartnerUsers = useCallback(async () => {
    if (!id) return;
    setUsersLoading(true);
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, full_name, role, created_at, email:auth.users!inner(email)')
        .eq('partner_id', id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const mapped = (profiles || []).map((p: any) => ({
        id: p.id,
        full_name: p.full_name,
        email: p.email?.email || p.email || '',
        role: p.role,
        created_at: p.created_at,
      }));
      setPartnerUsers(mapped);
    } catch {
      setPartnerUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchPartnerUsers();
    }
  }, [activeTab, fetchPartnerUsers]);

  // Mock fallbacks for tabs when no real data
  const displayOffers = offers.length > 0 ? offers : mockOffers.filter(o => o.partner_id === id && !o.archived_at);
  const displayLeads = leads.length > 0 ? leads : mockLeads.filter(l => l.assigned_partner_id === id && !l.deleted_at).map(l => ({
    id: l.id,
    assigned_partner_id: l.assigned_partner_id,
    status: l.status,
    routing_status: l.routing_status,
    created_at: l.created_at,
    raw_payload: null,
  }));
  const displayPayouts = payouts.length > 0 ? payouts : mockPayoutLines.filter(p => p.partner_id === id).map(p => ({
    id: p.id,
    partner_id: p.partner_id,
    payout_model: 'cpl',
    amount: p.amount_local,
    currency: p.currency,
    status: p.status,
    created_at: p.created_at,
  }));

  if (!partner) {
    return (
      <div className="text-center py-12">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Partner not found</p>
        <button onClick={() => navigate('/partners')} className="mt-2 text-sm transition-colors" style={{ color: 'var(--text-muted)' }}>
          Back to partners
        </button>
      </div>
    );
  }

  const tierBadge = (tier: string) => {
    switch (tier) {
      case 'strategic': return { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-glow)' };
      case 'preferred': return { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-glow)' };
      default: return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'paused': return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
      default: return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const offerStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'paused': return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
      case 'draft': return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
      case 'expired': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      default: return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const payoutStatusBadge = (status: string) => {
    switch (status) {
      case 'approved': return { bg: 'var(--success-bg)', color: 'var(--success-text)' };
      case 'paid': return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)' };
      case 'disputed': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)' };
      default: return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)' };
    }
  };

  const getLeadName = (l: DbLead) => {
    if (l.raw_payload && typeof l.raw_payload === 'object') {
      const rp = l.raw_payload as Record<string, unknown>;
      const fn = rp.firstname || rp.first_name || rp.name;
      const ln = rp.lastname || rp.last_name;
      if (fn) return `${fn}${ln ? ' ' + ln : ''}`;
    }
    return `Lead ${l.id.slice(-6)}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/partners')} className="p-1.5 rounded-lg transition-colors mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>{partner.display_name}</h1>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border" style={tierBadge(partner.partner_tier)}>
              {partner.partner_tier}
            </span>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{partner.legal_name} · {partner.country}</p>
        </div>
        {deleteConfirm ? (
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--danger-text)' }}>Delete this partner?</span>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border transition-all disabled:opacity-40"
              style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
            >
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              {deleting ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button
              onClick={() => setDeleteConfirm(false)}
              className="px-3 py-1.5 text-xs rounded-lg transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => editing ? handleSave() : setEditing(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all"
              style={editing ? { background: 'var(--accent)', color: 'var(--text-inverse)', borderColor: 'transparent' } : { color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
            >
              {editing ? <><Save size={12} /> Save</> : <><Edit2 size={12} /> Edit</>}
            </button>
            <button
              onClick={() => setDeleteConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors"
              style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl border text-sm"
          style={{ borderColor: 'var(--danger-border)', background: 'var(--danger-bg)', color: 'var(--danger-text)' }}
        >
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Active Offers', value: displayOffers.filter(o => o.status === 'active' || (o as any).active === true).length },
          { label: 'Total Leads', value: displayLeads.length },
          { label: 'Payouts', value: displayPayouts.length },
          { label: 'Reliability', value: `${(partner.reliability_score * 100).toFixed(1)}%` },
        ].map((s, i) => (
          <div key={i} className="rounded-xl border p-4 transition-all duration-300" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
            <p className="text-lg font-bold mt-1" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="border-b" style={{ borderColor: 'var(--border-separator)' }}>
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-all duration-200"
              style={{
                borderColor: activeTab === tab.id ? 'var(--accent)' : 'transparent',
                color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-tertiary)',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border p-5" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        {activeTab === 'info' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
            {[
              { label: 'Legal Name', value: partner.legal_name, key: 'legal_name' },
              { label: 'Display Name', value: partner.display_name, key: 'display_name' },
              { label: 'Country', value: partner.country, key: 'country' },
              { label: 'VAT ID', value: partner.vat_id || '—', key: 'vat_id' },
              { label: 'Billing Currency', value: partner.billing_currency, key: 'billing_currency' },
              { label: 'Primary Contact', value: partner.primary_contact_name || '—', key: 'primary_contact_name' },
            ].map(item => (
              <div key={item.label}>
                <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</label>
                {editing ? (
                  <input
                    type="text"
                    value={editData[item.key as keyof typeof editData]}
                    onChange={e => setEditData(prev => ({ ...prev, [item.key]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                  />
                ) : (
                  <p style={{ color: 'var(--text-secondary)' }}>{item.value}</p>
                )}
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Email</label>
              {editing ? (
                <input
                  type="email"
                  value={editData.primary_email}
                  onChange={e => setEditData(prev => ({ ...prev, primary_email: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                />
              ) : (
                <a href={`mailto:${partner.primary_email}`} className="flex items-center gap-1 transition-colors" style={{ color: 'var(--text-tertiary)' }}>
                  <Mail size={12} /> {partner.primary_email || '—'}
                </a>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={editData.primary_phone}
                  onChange={e => setEditData(prev => ({ ...prev, primary_phone: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                />
              ) : (
                <a href={`tel:${partner.primary_phone}`} className="flex items-center gap-1 transition-colors" style={{ color: 'var(--text-tertiary)' }}>
                  <Phone size={12} /> {partner.primary_phone || '—'}
                </a>
              )}
            </div>
            {partner.internal_notes && (
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Internal Notes</label>
                {editing ? (
                  <textarea
                    value={editData.internal_notes}
                    onChange={e => setEditData(prev => ({ ...prev, internal_notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg outline-none resize-none transition-all"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                  />
                ) : (
                  <p className="p-3 rounded-lg text-xs border" style={{ color: 'var(--text-tertiary)', background: 'var(--bg-input)', borderColor: 'var(--border-separator)' }}>
                    {partner.internal_notes}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'offers' && (
          loading ? <SkeletonList rows={3} /> : (
            <div className="space-y-2">
              {displayOffers.map(o => {
                const vertical = mockVerticals.find(v => v.id === o.vertical_id);
                const geo = mockGeos.find(g => g.id === o.geo_id);
                const rate = (o as any).payout_config?.rate_local || o.cpl_value || o.cpa_value || o.revshare_percent || o.flat_subscription_amount || '—';
                return (
                  <div key={o.id}
                    className="flex items-center justify-between p-3 rounded-xl border transition-colors hover-surface"
                    style={{ borderColor: 'var(--border-separator)' }}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{vertical?.name || o.vertical_id} — {geo?.name || o.geo_id}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border" style={offerStatusBadge(o.status)}>{o.status}</span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{o.payout_model.toUpperCase()} · Priority {(o as any).priority || o.priority_score}</p>
                    </div>
                    <button onClick={() => navigate(`/offers/${o.id}`)} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }}>
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                );
              })}
              {displayOffers.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No offers yet</p>}
            </div>
          )
        )}

        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <PartnerInviteModal partnerId={partner.id} onInvited={fetchPartnerUsers} />
            </div>
            {usersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
              </div>
            ) : partnerUsers.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No users linked to this partner yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {partnerUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: 'var(--border-separator)' }}>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{u.full_name || 'Unnamed'}</p>
                      <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{u.email} · {u.role}</p>
                    </div>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          loading ? <SkeletonList rows={5} /> : (
            <div className="space-y-2">
              {displayLeads.slice(0, 20).map(l => (
                <div key={l.id}
                  className="flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors hover-surface"
                  style={{ borderColor: 'var(--border-separator)' }}
                  onClick={() => navigate(`/leads/${l.id}`)}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{getLeadName(l)}</p>
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{l.status} · {l.routing_status}</p>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(l.created_at).toLocaleDateString()}</span>
                </div>
              ))}
              {displayLeads.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No activity yet</p>}
            </div>
          )
        )}

        {activeTab === 'payouts' && (
          loading ? <SkeletonList rows={4} /> : (
            <div className="space-y-2">
              {displayPayouts.map(p => (
                <div key={p.id}
                  className="flex items-center justify-between p-3 rounded-xl border transition-colors hover-surface"
                  style={{ borderColor: 'var(--border-separator)' }}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{p.payout_model.toUpperCase()}</p>
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{p.currency} {p.amount}</p>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium" style={payoutStatusBadge(p.status)}>{p.status}</span>
                </div>
              ))}
              {displayPayouts.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No payouts yet</p>}
            </div>
          )
        )}
      </div>
    </div>
  );
}